import styles from './GlobalCopilotOverlay.module.css'
import {ActionList, Button, IconButton, LinkButton, RelativeTime} from '@primer/react'
import {Blankslate, SkeletonAvatar, SkeletonText} from '@primer/react/experimental'
import {AlertIcon, CommentIcon, DotFillIcon, GearIcon, PlusIcon} from '@primer/octicons-react'
import {Icon} from '@github-ui/pacer/Icon'
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/components/TaskListItem'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import type {HydratedTask, NewTaskPayload} from '@github-ui/agent-sessions/types/task'
import {CommandIconButton} from '@github-ui/ui-commands'
import {NewAgentTaskForm} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {useCopilotChatPayload} from './hooks/useCopilotChatPayload'
import {useMemo, useState} from 'react'

export function GlobalCopilotOverlay({
  onClose,
  pendingTaskFromEvent,
}: {
  onClose?: () => void
  pendingTaskFromEvent?: NewTaskPayload
}) {
  return (
    <ConsecutiveCAPI401sProvider>
      <GlobalCopilotOverlayContent onClose={onClose} pendingTaskFromEvent={pendingTaskFromEvent} />
    </ConsecutiveCAPI401sProvider>
  )
}

function GlobalCopilotOverlayContent({
  onClose,
  pendingTaskFromEvent,
}: {
  onClose?: () => void
  pendingTaskFromEvent?: NewTaskPayload
}) {
  // Once this PR (https://github.com/github/github/pull/392100) merges, we'll be able to remove this as it's duplicative of what's already in
  // NewAgentTaskForm. We just need it here in this component now to pass an apiURL into useTasks below, but we should move to just using getEnv().copilotApiOverrideUrl
  // inside useTasks in the future.
  const copilotChatPayload = useCopilotChatPayload()
  const {tasks, pendingTasks, isLoading, isError, createSession} = useTasks({apiURL: copilotChatPayload?.apiURL ?? ''})
  const [newTaskMode, setNewTaskMode] = useState(false)

  // Combine the pending task from the event with the tasks from the hook
  const allPendingTasks = useMemo(() => {
    if (pendingTaskFromEvent) {
      // Check if the task from the event has already been created server side
      const isSuccessfullyCreated = tasks.some(task => task.event_identifiers?.includes(pendingTaskFromEvent.id))

      if (!isSuccessfullyCreated) {
        return [pendingTaskFromEvent, ...pendingTasks]
      }
    }

    return pendingTasks
  }, [tasks, pendingTasks, pendingTaskFromEvent])

  // We use this to close the overlay on link clicks so the user has some feedback before the page changes.
  const handleLinkClick = (event: React.MouseEvent) => {
    // Only close if it's not a cmd+click (metaKey on Mac) or ctrl+click (ctrlKey on Windows/Linux)
    // These are used to open links in new tabs
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    const isModifiedClick = event.metaKey || event.ctrlKey
    if (!isModifiedClick && onClose) {
      onClose()
    }
  }

  return (
    <div className={styles.overlay}>
      {newTaskMode ? (
        <NewAgentTaskForm
          createSession={createSession}
          onCreate={() => setNewTaskMode(false)}
          onCancel={() => setNewTaskMode(false)}
          eventType="agents_panel"
        />
      ) : (
        <>
          <OverlayHeader setNewTaskMode={setNewTaskMode} onClose={onClose} onLinkClick={handleLinkClick} />
          <OverlayContent
            tasks={tasks}
            pendingTasks={allPendingTasks}
            isLoading={isLoading}
            isError={isError}
            onLinkClick={handleLinkClick}
            setNewTaskMode={setNewTaskMode}
          />
          <OverlayFooter tasks={tasks} pendingTasks={pendingTasks} />
        </>
      )}
    </div>
  )
}

function OverlayHeader({
  setNewTaskMode,
  onClose,
  onLinkClick,
}: {
  setNewTaskMode: (value: boolean) => void
  onClose?: () => void
  onLinkClick: (event: React.MouseEvent) => void
}) {
  return (
    <div className={styles.header}>
      <h2 className="f5">Recent agent tasks</h2>
      <div className={styles.headerActions}>
        <IconButton
          variant="invisible"
          aria-label="New agent task"
          onClick={() => setNewTaskMode(true)}
          icon={PlusIcon}
        />
        <CommandIconButton
          variant="invisible"
          aria-label="Open assistive chat"
          icon={CommentIcon}
          commandId="copilot-chat:open-assistive"
          onClick={onClose}
        />
        <IconButton
          variant="invisible"
          aria-label="Copilot settings"
          icon={GearIcon}
          as="a"
          href="/settings/copilot"
          onClick={onLinkClick}
        />
      </div>
    </div>
  )
}

export function OverlayContent({
  tasks,
  pendingTasks,
  isLoading,
  isError,
  onLinkClick,
  setNewTaskMode,
}: {
  tasks: HydratedTask[]
  pendingTasks: NewTaskPayload[]
  isLoading: boolean
  isError: boolean
  onLinkClick: (event: React.MouseEvent) => void
  setNewTaskMode: (value: boolean) => void
}) {
  return (
    // TODO: Add conditional shadows on top and bottom when content is hidden by overflow in either direction.
    <div className={styles.content}>
      {isLoading ? (
        <TasksLoadingState />
      ) : isError ? (
        <TasksErrorState />
      ) : pendingTasks.length === 0 && tasks.length === 0 ? (
        <TasksEmptyState setNewTaskMode={setNewTaskMode} />
      ) : (
        <TasksList tasks={tasks} pendingTasks={pendingTasks} onLinkClick={onLinkClick} />
      )}
    </div>
  )
}

export function OverlayFooter({tasks, pendingTasks}: {tasks: HydratedTask[]; pendingTasks: NewTaskPayload[]}) {
  return (
    tasks.length + pendingTasks.length > 0 && (
      <div className={styles.footer}>
        <LinkButton variant="invisible" size="small" block href="/copilot/agents">
          View all tasks
        </LinkButton>
      </div>
    )
  )
}

function TasksList({
  tasks,
  pendingTasks,
  onLinkClick,
}: {
  tasks: HydratedTask[]
  pendingTasks: NewTaskPayload[]
  onLinkClick: (event: React.MouseEvent) => void
}) {
  return (
    <ActionList showDividers>
      {pendingTasks.map(pendingTask => {
        return (
          <ActionList.Item key={pendingTask.submittedAt} className={styles.task}>
            <ActionList.LeadingVisual>
              <Icon icon={DotFillIcon} color={'var(--fgColor-attention)' as const} />
            </ActionList.LeadingVisual>
            {`Creating: ${pendingTask.problemStatement}`}
            <ActionList.Description variant="block" className="no-wrap">
              {pendingTask.nwo}
            </ActionList.Description>
          </ActionList.Item>
        )
      })}
      {tasks.map(task => {
        const iconAttributes = getLeadingVisualIconAttributes(task.pull, task.latest_session_state)
        return (
          <ActionList.LinkItem key={task.pull.id} className={styles.task} href={task.pull.url} onClick={onLinkClick}>
            <ActionList.LeadingVisual>
              <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
            </ActionList.LeadingVisual>
            {task.pull.title}
            <ActionList.Description variant="block" className="no-wrap">
              {task.pull.repository_nwo} ·{' '}
              <RelativeTime datetime={task.latest_session_updated_at} format="micro" className={styles.taskStartDate} />
            </ActionList.Description>
          </ActionList.LinkItem>
        )
      })}
    </ActionList>
  )
}

function TasksLoadingState() {
  return (
    <ActionList showDividers>
      {Array.from({length: 3}, (_, index) => (
        <ActionList.Item key={index} disabled>
          <ActionList.LeadingVisual>
            <SkeletonAvatar square size={16} />
          </ActionList.LeadingVisual>
          {/* eslint-disable-next-line primer-react/no-system-props */}
          <SkeletonText maxWidth="85%" />
          <ActionList.Description variant="block">
            {/* eslint-disable-next-line primer-react/no-system-props */}
            <SkeletonText size="bodySmall" maxWidth="50%" />
          </ActionList.Description>
        </ActionList.Item>
      ))}
    </ActionList>
  )
}

function TasksEmptyState({setNewTaskMode}: {setNewTaskMode: (value: boolean) => void}) {
  return (
    <div className={styles.emptyState}>
      <h3 className={styles.emptyStateHeading}>No tasks yet</h3>
      <p className={styles.emptyStateDescription}>Assign a task to an agent to update your code.</p>
      <Button onClick={() => setNewTaskMode(true)}>New task</Button>
    </div>
  )
}

function TasksErrorState() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Tasks can’t be loaded</Blankslate.Heading>
      <Blankslate.Description className="text-center text-wrap-balance">
        Try again or, if the problem persists, contact support.
      </Blankslate.Description>
      <Blankslate.SecondaryAction href="https://githubstatus.com/">GitHub status</Blankslate.SecondaryAction>
    </Blankslate>
  )
}

try{ GlobalCopilotOverlay.displayName ||= 'GlobalCopilotOverlay' } catch {}
try{ GlobalCopilotOverlayContent.displayName ||= 'GlobalCopilotOverlayContent' } catch {}
try{ OverlayHeader.displayName ||= 'OverlayHeader' } catch {}
try{ OverlayContent.displayName ||= 'OverlayContent' } catch {}
try{ OverlayFooter.displayName ||= 'OverlayFooter' } catch {}
try{ TasksList.displayName ||= 'TasksList' } catch {}
try{ TasksLoadingState.displayName ||= 'TasksLoadingState' } catch {}
try{ TasksEmptyState.displayName ||= 'TasksEmptyState' } catch {}
try{ TasksErrorState.displayName ||= 'TasksErrorState' } catch {}